import { useEffect, useState, useRef } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, DoorOpen, Upload, X, ImageIcon } from "lucide-react";

const EQUIPMENT = ["Skærm", "Whiteboard", "Videoudstyr", "Kaffemaskine", "Køleskab", "Telefon"];
const COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#0EA5E9", "#F43F5E", "#14B8A6"];

const empty = {
  name: "",
  description: "",
  capacity: 4,
  equipment: [],
  image_url: "",
  color: COLORS[0],
  active: true,
  allowed_companies: [],
  floor: "",
};

export default function AdminRooms() {
  const [rooms, setRooms] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Build full URL for displaying images (handle both absolute URLs and our /api/files paths)
  const imageSrc = (url) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    if (url.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    return url;
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Billede er for stort (max 5MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload/room-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image_url: res.data.url }));
      toast.success("Billede uploadet");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const load = async () => {
    try {
      const [r, c] = await Promise.all([api.get("/rooms"), api.get("/companies")]);
      setRooms(r.data);
      setCompanies(c.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm(empty);
    setEditId(null);
    setOpen(true);
  };

  const openEdit = (r) => {
    setForm({
      name: r.name,
      description: r.description || "",
      capacity: r.capacity,
      equipment: r.equipment || [],
      image_url: r.image_url || "",
      color: r.color,
      active: r.active,
      allowed_companies: r.allowed_companies || [],
      floor: r.floor || "",
    });
    setEditId(r.id);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        await api.put(`/rooms/${editId}`, form);
        toast.success("Lokale opdateret");
      } else {
        await api.post("/rooms", form);
        toast.success("Lokale oprettet");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Slet dette lokale? Alle bookinger fjernes.")) return;
    try {
      await api.delete(`/rooms/${id}`);
      toast.success("Slettet");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleEq = (e) => {
    setForm((f) => ({
      ...f,
      equipment: f.equipment.includes(e)
        ? f.equipment.filter((x) => x !== e)
        : [...f.equipment, e],
    }));
  };

  const toggleCo = (id) => {
    setForm((f) => ({
      ...f,
      allowed_companies: f.allowed_companies.includes(id)
        ? f.allowed_companies.filter((x) => x !== id)
        : [...f.allowed_companies, id],
    }));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Lokaler</h1>
          <p className="text-slate-500 mt-1">Administrer lokaler i kontorhuset</p>
        </div>
        <Button onClick={openNew} className="rounded-xl bg-slate-900 hover:bg-slate-800 gap-2 h-11" data-testid="new-room-button">
          <Plus className="h-4 w-4" /> Nyt lokale
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rooms.map((r) => (
          <Card key={r.id} className="rounded-2xl overflow-hidden border-slate-200" data-testid={`room-card-${r.id}`}>
            <div
              className="h-32 relative bg-slate-100"
              style={{
                background: r.image_url ? `url(${imageSrc(r.image_url)}) center/cover` : r.color,
              }}
            >
              <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-semibold text-white" style={{ background: r.color }}>
                {r.floor || "—"}
              </div>
              {!r.active && (
                <div className="absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-semibold bg-black/70 text-white">
                  Inaktiv
                </div>
              )}
            </div>
            <div className="p-5 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-display font-semibold text-lg">{r.name}</h3>
                  <p className="text-xs text-slate-500">Kapacitet: {r.capacity}</p>
                </div>
              </div>
              {r.description && <p className="text-sm text-slate-600">{r.description}</p>}
              {r.equipment?.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {r.equipment.map((e) => (
                    <span key={e} className="text-[11px] px-2 py-0.5 bg-slate-100 rounded-full">
                      {e}
                    </span>
                  ))}
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <Button variant="outline" size="sm" className="rounded-xl flex-1" onClick={() => openEdit(r)} data-testid={`edit-room-${r.id}`}>
                  <Pencil className="h-3 w-3 mr-1" /> Rediger
                </Button>
                <Button variant="outline" size="sm" className="rounded-xl text-red-600" onClick={() => remove(r.id)} data-testid={`delete-room-${r.id}`}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
        {rooms.length === 0 && (
          <Card className="p-10 rounded-2xl border-dashed text-center col-span-full">
            <DoorOpen className="h-8 w-8 mx-auto text-slate-300 mb-2" />
            <p className="text-slate-500">Ingen lokaler endnu</p>
          </Card>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[560px] rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editId ? "Rediger lokale" : "Nyt lokale"}
            </DialogTitle>
            <DialogDescription>
              Udfyld lokalets detaljer, vælg en farve og upload et billede hvis ønsket.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="room-form">
            <div className="space-y-2">
              <Label>Navn</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl h-11" required data-testid="room-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Beskrivelse</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Kapacitet</Label>
                <Input type="number" min={1} value={form.capacity} onChange={(e) => setForm({ ...form, capacity: parseInt(e.target.value) || 1 })} className="rounded-xl h-11" />
              </div>
              <div className="space-y-2">
                <Label>Etage</Label>
                <Input value={form.floor} onChange={(e) => setForm({ ...form, floor: e.target.value })} placeholder="Fx Stuen / 1. sal" className="rounded-xl h-11" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Billede</Label>
              {form.image_url ? (
                <div className="relative rounded-xl overflow-hidden border border-slate-200 group">
                  <img
                    src={imageSrc(form.image_url)}
                    alt="Lokale forhåndsvisning"
                    className="w-full h-40 object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                    data-testid="room-image-preview"
                  />
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, image_url: "" })}
                    className="absolute top-2 right-2 bg-black/70 hover:bg-black/90 text-white p-1.5 rounded-lg transition-colors"
                    data-testid="remove-room-image"
                    title="Fjern billede"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-center h-40 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50">
                  <div className="text-center">
                    <ImageIcon className="h-8 w-8 mx-auto text-slate-300 mb-1" />
                    <p className="text-xs text-slate-400">Intet billede valgt</p>
                  </div>
                </div>
              )}
              <div className="flex gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleFileUpload}
                  className="hidden"
                  data-testid="room-image-file-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="rounded-xl flex-1 gap-2"
                  data-testid="upload-room-image-button"
                >
                  <Upload className="h-4 w-4" />
                  {uploading ? "Uploader…" : form.image_url ? "Skift billede" : "Upload billede"}
                </Button>
              </div>
              <p className="text-xs text-slate-400">JPEG, PNG, WebP eller GIF · max 5MB</p>
            </div>
            <div className="space-y-2">
              <Label>Farve</Label>
              <div className="flex flex-wrap gap-2">
                {COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setForm({ ...form, color: c })}
                    className={`w-9 h-9 rounded-xl transition-all ${form.color === c ? "ring-2 ring-offset-2 ring-slate-900 scale-110" : ""}`}
                    style={{ background: c }}
                    data-testid={`color-${c}`}
                  />
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Udstyr</Label>
              <div className="grid grid-cols-2 gap-2">
                {EQUIPMENT.map((e) => (
                  <label key={e} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={form.equipment.includes(e)} onCheckedChange={() => toggleEq(e)} />
                    {e}
                  </label>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Virksomheder med adgang (tom = alle)</Label>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {companies.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={form.allowed_companies.includes(c.id)} onCheckedChange={() => toggleCo(c.id)} />
                    {c.name}
                  </label>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
              <Label className="cursor-pointer">Aktiv</Label>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="room-active-switch" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-xl">
                Annullér
              </Button>
              <Button type="submit" className="rounded-xl bg-slate-900 hover:bg-slate-800" data-testid="save-room-button">
                Gem
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
